
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
  formatFractionLatexCore
} from "../../../../components/inputs/FractionInput";



function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function parseExpression(expr) {
  return expr
    .replace(/\+\s*-/g, "- ")
    .replace(/-\s*-/g, "+ ")
    .replace(/\s+/g, " ")
    .trim();
}
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}


function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  // Szukamy ułamka skracalnego z dokładnością do 1/100
  const maxDen = 10; // np. 10 → pozwala na 1/2, 1/3, ..., 9/10
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  // fallback jeśli nie znaleziono ładnej postaci
  return `${sign}${abs.toFixed(2)}`;
}



export function generateTask() {
  const getRandomInt = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  // Współczynnik kierunkowy oryginalnej prostej
  let num, den;
  do {
    num = getRandomInt(-5, 5);
    den = getRandomInt(1, 5);
  } while (num === 0 || num % den === 0); // unikamy 0 i całkowitych

  const aOriginal = num / den;
  const a = -den / num; // prostopadły współczynnik

  // Punkt, przez który ma przechodzić prosta
  const x = getRandomInt(-5, 5);
  const y = getRandomInt(-5, 5);

  const newB = y - a * x; // b = y - ax

  // b do wyświetlenia w równaniu bazowym (nieistotne dla obliczeń)
  let displayNum = getRandomInt(-10, 10);
  let displayDen = getRandomInt(1, 4);
  const b = displayNum / displayDen;

  return {
    type: "PerpendicularLineThroughPointTask",
    data: { a, x, y, aOriginal, b, newB },
    answer: {
      a: { raw: `-${den}/${num}`, parsed: a },
      b: { raw: `${newB}`, parsed: newB }
    }
  };
}

export function renderTask(task) {
  const { aOriginal, b, x, y } = task.data;

  const aLatex = formatMixedFractionLatex(aOriginal);
  const bLatexRaw = formatMixedFractionLatex(b);

  const bLatex =
    b === 0 ? "" :
    b > 0 ? ` + ${bLatexRaw}` :
    ` - ${bLatexRaw.slice(1)}`; // usuwamy minus, bo już dodaliśmy znak "-"

  return (
    <div className="space-y-2">
      <p>
        Napisz równanie prostej <strong>prostopadłej</strong> do prostej{" "}
        <InlineMath math={`y = ${aLatex}x${bLatex}`} />{" "}
        i przechodzącej przez punkt <InlineMath math={`P\\,=\\,(${x}, ${y})`} />.
      </p>
    </div>
  );
}


export function renderAnswer({ data }) {
  const { a, newB } = data;

  const aLatex = formatMixedFractionLatex(a);
  const bLatex = formatMixedFractionLatex(newB);

  const bFinal =
    newB === 0 ? "" :
    newB > 0 ? ` + ${bLatex}` :
    ` - ${bLatex.slice(1)}`; // usuwamy minus, bo dodaliśmy znak ręcznie

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={`\\displaystyle y = ${aLatex}x${bFinal}`} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer || typeof userAnswer !== "object" ||
    userAnswer.a?.parsed == null || userAnswer.b?.parsed == null
  ) {
    return false;
  }

  const aUser = userAnswer.a.parsed;
  const bUser = userAnswer.b.parsed;

  const aExpected = data.a;
  const bExpected = data.newB;

  const isAOk = Math.abs(aExpected - aUser) < 0.01;
  const isBOk = Math.abs(bExpected - bUser) < 0.01;

  return isAOk && isBOk;
}

export function renderExplanation({ data }) {
  const { a, x, y, newB, aOriginal } = data;

  const aLatex = formatMixedFractionLatex(aOriginal);
  const reciprocal = -1 / aOriginal;
  const negReciprocalLatex = formatMixedFractionLatex(reciprocal);

  const bLatex = formatMixedFractionLatex(newB);


  const xFormatted = x < 0 ? `(${x})` : `${x}`;
  const bRendered =
  newB === 0 ? "" :
  newB > 0 ? ` + ${bLatex}` :
  ` - ${bLatex.slice(1)}`; // usuwamy minus z frontu ułamka

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
<div>
  <strong>Krok 1:</strong> Prosta <strong>prostopadła</strong> ma współczynnik kierunkowy odwrotny i przeciwny do danej prostej:
<BlockMath math={`a_p = -\\frac{1}{a}`} />
<BlockMath math={`a_p = -\\frac{1}{${aLatex}} = ${negReciprocalLatex}`} />

</div>


      <div>
        <strong>Krok 2:</strong> Podstawiamy współrzędne punktu <InlineMath math={`(${x}, ${y})`} /> do wzoru ogólnego:
        <BlockMath math={`y = ax + b`} />
        <BlockMath math={`${y} = ${aLatex} \\cdot ${xFormatted} + b`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Obliczamy <InlineMath math="b" />, przenosząc iloczyn na lewą stronę:
        <BlockMath math={`b = ${y} - (${aLatex} \\cdot ${xFormatted}) = ${bLatex}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Równanie prostej ma postać:
        <BlockMath math={`y = ${aLatex}x${bRendered}`} />
      </div>
    </div>
  );
}


export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <InlineMath math="a=" />
        <FractionInput
          value={userAnswer?.a || ""}
          onChange={(val) => onChange({ ...userAnswer, a: val })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math="b=" />
        <FractionInput
          value={userAnswer?.b || ""}
          onChange={(val) => onChange({ ...userAnswer, b: val })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}


export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.a?.raw ||
    !userAnswer.b?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latexA = formatLatexPreview(userAnswer.a.raw, "a");
  const latexB = formatLatexPreview(userAnswer.b.raw, "b");

  const aCore = formatFractionLatexCore(userAnswer.a.raw);
  const bCore = formatFractionLatexCore(userAnswer.b.raw);

  const bClean = bCore.startsWith("-") ? bCore.slice(1) : bCore;
  const sign = bCore.startsWith("-") ? "-" : "+";

  const equation = `y = ${aCore}x ${sign} ${bClean}`;

  return (
    <div className="flex flex-wrap gap-y-4">
      <div style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
        <InlineMath math={latexA} />
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={latexB} />
      </div>
      <div className="pt-2">
        <strong>Równanie prostej:</strong>{" "}
        <InlineMath math={equation} />
      </div>
    </div>
  );
}
